import { useBookerStoreContext } from "@calcom/features/bookings/Booker/BookerStoreProvider";
import { Dialog } from "@calcom/features/components/controlled-dialog";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui/components/button";
import { DialogClose, DialogContent, DialogFooter, DialogHeader } from "@calcom/ui/components/dialog";
import { Input, Label } from "@calcom/ui/components/form";
import { InfoIcon } from "@coss/ui/icons";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useState } from "react";
import useDigitInput from "react-digit-input";

export const SmsVerifyCodeDialog = ({
  isOpenDialog,
  setIsOpenDialog,
  phoneNumber,
  onVerify,
  resetErrors,
  setIsPending,
  isPending,
  error,
}: {
  isOpenDialog: boolean;
  setIsOpenDialog: Dispatch<SetStateAction<boolean>>;
  phoneNumber: string;
  onVerify: (code: string, phone: string) => void;
  resetErrors: () => void;
  isPending: boolean;
  setIsPending: (status: boolean) => void;
  error: string;
}) => {
  const { t } = useLocale();
  const [value, setValue] = useState("");
  const [hasVerified, setHasVerified] = useState(false);
  const setVerificationCode = useBookerStoreContext((state) => state.setVerificationCode);

  const digits = useDigitInput({
    acceptedCharacters: /^[0-9]$/,
    length: 6,
    value,
    onChange: useCallback(
      (value: string) => {
        resetErrors();
        setValue(value);
      },
      [resetErrors]
    ),
  });

  const verifyCode = useCallback(() => {
    resetErrors();
    setIsPending(true);
    onVerify(value, phoneNumber);
    setVerificationCode(value);
    setHasVerified(true);
  }, [resetErrors, setIsPending, onVerify, value, phoneNumber, setVerificationCode]);

  useEffect(() => {
    if (hasVerified || error || isPending || !/^\d{6}$/.test(value.trim())) return;
    verifyCode();
  }, [error, isPending, value, hasVerified, verifyCode]);

  useEffect(() => setValue(""), []);

  const maskedPhone = phoneNumber ? `${phoneNumber.slice(0, 4)}****${phoneNumber.slice(-2)}` : "";

  const digitClassName =
    "h-12 w-12 text-center text-xl! text-emphasis caret-emphasis [-webkit-text-fill-color:currentColor]";

  return (
    <Dialog
      open={isOpenDialog}
      onOpenChange={() => {
        resetErrors();
      }}>
      <DialogContent className="sm:max-w-md">
        <div className="flex flex-row">
          <div className="w-full">
            <DialogHeader
              title={t("verify_your_phone") || "Telefonunuzu Doğrulayın"}
              subtitle={
                t("enter_sms_code", { phone: maskedPhone }) ||
                `${maskedPhone} numarasına gönderilen 6 haneli kodu girin`
              }
            />
            <Label htmlFor="code">{t("code") || "Kod"}</Label>
            <div className="flex flex-row justify-between">
              <Input
                className={digitClassName}
                name="sms1"
                inputMode="decimal"
                {...digits[0]}
                autoFocus
                autoComplete="one-time-code"
              />
              <Input className={digitClassName} name="sms2" inputMode="decimal" {...digits[1]} />
              <Input className={digitClassName} name="sms3" inputMode="decimal" {...digits[2]} />
              <Input className={digitClassName} name="sms4" inputMode="decimal" {...digits[3]} />
              <Input className={digitClassName} name="sms5" inputMode="decimal" {...digits[4]} />
              <Input className={digitClassName} name="sms6" inputMode="decimal" {...digits[5]} />
            </div>
            {error && (
              <div className="mt-2 flex items-center gap-x-2 text-red-700 text-sm">
                <div>
                  <InfoIcon className="h-3 w-3" />
                </div>
                <p>{error}</p>
              </div>
            )}
            <DialogFooter noSticky>
              <DialogClose onClick={() => setIsOpenDialog(false)} />
              <Button type="submit" onClick={verifyCode} loading={isPending}>
                {t("submit") || "Gönder"}
              </Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
